import * as yup from 'yup';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Button,
  useTheme,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColorPickerButton } from '../../buttons/ColorPickerButton';
import {
  CATEGORIES_PER_KEYS,
  CATEGORIES_SORTED,
} from '../../../types/Categories';
import { SelectCategoryModal } from '../../modals/SelectCategoryModal';

interface PrefilledLineEditorFormValues {
  name: string;
  checked: boolean;
  type: string;
  description: string;
  amount: string;
  color: string;
  category?: string;
}

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  checked: yup.boolean().required(),
  type: yup.string(),
  description: yup.string(),
  amount: yup.lazy((value) =>
    value === '' ? yup.string().oneOf(['']) : yup.number(),
  ), // Allow cast from string.
  color: yup.string().matches(/^#[0-9ABCDEFabcdef]{6}$/),
  category: yup
    .string()
    .notRequired()
    .oneOf(CATEGORIES_SORTED.map((category) => category.key)),
});

const usePrefilledLineEditorForm = (
  defaultValues: PrefilledLineEditorFormValues,
  onSubmit: (values: PrefilledLineEditorFormValues) => void,
) => {
  const form = useForm<PrefilledLineEditorFormValues>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: 'onBlur',
  });

  const handleFormSubmit = useMemo(
    () => form.handleSubmit(onSubmit),
    [form, onSubmit],
  );

  const resetValues = useCallback(
    (values: PrefilledLineEditorFormValues) => {
      form.reset(values);
    },
    [form],
  );

  return useMemo(
    () => ({
      form,
      handleFormSubmit,
      resetValues,
    }),
    [form, handleFormSubmit, resetValues],
  );
};

interface Props {
  form: ReturnType<typeof usePrefilledLineEditorForm>;
}

const rowContainerSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '& > :not(:first-of-type)': {
    marginLeft: 0.5,
  },
  '& > :not(:last-child)': {
    marginRight: 0.5,
  },
} as const;

const colorFieldContainerSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
} as const;

const PrefilledLineEditComponent = (props: Props) => {
  const theme = useTheme();
  const { form, handleFormSubmit } = props.form;
  const {
    register,
    control,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    watch,
  } = form;

  const categoryValue = watch('category');
  const colorValue = watch('color');

  const category = categoryValue
    ? CATEGORIES_PER_KEYS[categoryValue]
    : undefined;
  const categoryDisplay = category ? category.name : 'Aucune...';

  const categoryColor = category?.color;
  const categoryTextColor = categoryColor
    ? theme.palette.getContrastText(categoryColor)
    : undefined;

  const [selectCategoryModalOpen, setSelectCategoryModalOpen] = useState(false);

  return (
    <>
      <SelectCategoryModal
        open={selectCategoryModalOpen}
        onCategorySelected={(category) => {
          setSelectCategoryModalOpen(false);
          setValue('category', category?.key, { shouldTouch: true });
        }}
        onCancel={() => {
          setSelectCategoryModalOpen(false);
          trigger('category');
        }}
      />
      <Box
        component="form"
        sx={{ '& > *': { margin: (theme) => theme.spacing(1, 0) } }}
        onSubmit={handleFormSubmit}
      >
        <TextField
          fullWidth
          {...register('name')}
          label={'Nom'}
          variant="filled"
          error={touchedFields.name === true && errors.name != null}
          helperText={
            touchedFields.name === true && errors.name != null
              ? 'Un nom est requis.'
              : ' '
          }
        />
        <Controller
          name="checked"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label={'Encaissé'}
              control={
                <Checkbox
                  checked={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              }
            />
          )}
        />
        <Box sx={rowContainerSx}>
          <TextField
            fullWidth
            {...register('type')}
            label={'Type'}
            variant="filled"
            error={touchedFields.type === true && errors.type != null}
            helperText={
              touchedFields.type === true && errors.type != null
                ? 'Type invalide.'
                : ' '
            }
          />
          <TextField
            fullWidth
            {...register('description')}
            label={'Description'}
            variant="filled"
            error={
              touchedFields.description === true && errors.description != null
            }
            helperText={
              touchedFields.description === true && errors.description != null
                ? 'Description invalide.'
                : ' '
            }
          />
        </Box>
        <TextField
          fullWidth
          {...register('amount')}
          label={'Montant'}
          variant="filled"
          error={touchedFields.amount === true && errors.amount != null}
          helperText={
            touchedFields.amount === true && errors.amount != null
              ? 'Une valeur numérique ou vide doit être saisie.'
              : ' '
          }
        />
        <Box sx={rowContainerSx}>
          <Box sx={colorFieldContainerSx}>
            <Box
              component="label"
              sx={{
                color: 'grey.700',
                fontSize: '1rem',
                marginBottom: 1,
              }}
            >
              Couleur de la ligne
            </Box>
            <br />
            <ColorPickerButton
              variant={'contained'}
              buttonText={'Définir...'}
              colorValue={colorValue}
              onColorValueSubmit={(color) =>
                setValue('color', color, { shouldTouch: true })
              }
              onBlur={() => trigger('color')}
            />
          </Box>
          <Box sx={colorFieldContainerSx}>
            <Box
              component="label"
              sx={{
                color: 'grey.700',
                fontSize: '1rem',
                marginBottom: 1,
              }}
            >
              Catégorie de la ligne
            </Box>
            <br />
            <Button
              variant={'contained'}
              style={{
                backgroundColor: categoryColor,
                color: categoryTextColor,
              }}
              onClick={() => setSelectCategoryModalOpen(true)}
            >
              {category ? (
                <category.icon
                  htmlColor={categoryTextColor}
                  style={{ marginRight: 8 }}
                />
              ) : null}
              {categoryDisplay}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export const PrefilledLineEditorForm = {
  Component: PrefilledLineEditComponent,
  useForm: usePrefilledLineEditorForm,
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export declare namespace PrefilledLineEditorForm {
  export type ComponentProps = Props;
  export type FormValues = PrefilledLineEditorFormValues;
  export type FormState = ReturnType<typeof usePrefilledLineEditorForm>;
}
